import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check } from "lucide-react";
import { listGroupBookings, type BookingWithClient } from "@/lib/bookingsApi";
import { formatDisplayDate, formatTime } from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";
import { siteUrl } from "@/lib/supabase";
import { PaymentStatusBadge } from "./StatusBadge";

/** The whole block of performances for one client, with block totals and the pay-all link. */
const GroupPanel = ({ groupId, currentId }: { groupId: string; currentId: string }) => {
  const [rows, setRows] = useState<BookingWithClient[]>([]);
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void listGroupBookings(groupId).then((r) => {
      if (r.ok) {
        setRows(r.data.bookings.filter((b) => b.booking_status !== "archived"));
        setToken(r.data.pay_token);
      }
    });
  }, [groupId, currentId]);

  if (rows.length < 2) return null;
  const link = `${siteUrl || window.location.origin}/booking/pay-group/${token}`;
  const live = rows.filter((r) => r.booking_status !== "cancelled");
  const total = live.reduce((s, r) => s + r.fee_pence, 0);
  const owed = live.filter((r) => r.payment_status !== "paid" && r.payment_status !== "not_required").reduce((s, r) => s + r.fee_pence, 0);
  const fuel = live.reduce((s, r) => s + (r.fuel_charge_pence ?? 0), 0);

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="admin-card p-5 md:p-6 space-y-4 border-primary/30">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80 font-inter">Booking block</p>
          <h2 className="font-playfair text-2xl">{rows[0].clients?.venue_or_client_name} · {live.length} performances</h2>
        </div>
        <div className="text-right font-inter">
          <p className="text-[11px] uppercase tracking-widest text-white/45">Block total</p>
          <p className="text-primary text-2xl leading-tight">{formatGbpFromPence(total)}</p>
          <p className="text-xs text-white/45">{fuel > 0 ? `incl. ${formatGbpFromPence(fuel)} fuel · ` : ""}{owed > 0 ? `${formatGbpFromPence(owed)} still to pay` : "All paid"}</p>
        </div>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id}>
            <Link
              to={`/admin/bookings/${r.id}`}
              className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm font-inter transition-colors ${
                r.id === currentId ? "border-primary/50 bg-primary/10" : "border-white/10 hover:border-primary/30"
              }`}
            >
              <span className="min-w-0">
                <span className="block">{formatDisplayDate(r.performance_date)}{r.start_time ? ` · ${formatTime(r.start_time)}${r.end_time ? ` to ${formatTime(r.end_time)}` : ""}` : ""}</span>
                <span className="block text-xs text-white/40">{r.reference}{r.id === currentId ? " · viewing" : " · tap to edit"}</span>
              </span>
              <span className="flex items-center gap-3 shrink-0">
                {formatGbpFromPence(r.fee_pence)}
                <PaymentStatusBadge paymentStatus={r.payment_status} bookingStatus={r.booking_status} dueDate={r.due_date} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
        <p className="text-sm font-inter text-white/70">
          Send this one link. The client sees every performance, can pay all together or one at a time, and can download one invoice for everything or each on its own.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input readOnly value={link} className="admin-field flex-1 rounded-full bg-black/40 border border-white/10 px-4 py-2 text-xs font-inter text-white/70" />
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-xs uppercase tracking-widest"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupPanel;
